import React, { forwardRef } from "react";
import { IconChevronDown } from "@tabler/icons-react";
import useDrawerStyles from "../style";
import RemoteSearchSelect from "../../RemoteSearchSelect";
import { searchSkypointProviders } from "../../../services/skypointProvider";

// How a provider is named across the quarterly reports
export const providerLabel = (p) =>
  p?.businessName || p?.businessProfile?.companyName || p?.name || "Provider";

// The EHR a provider documents in
const ehrOf = (p) => (p?.isExternal ? p?.ehrSystem || "" : "Ogiso");

// An option, with the provider's EHR after its name when it has one
const ProviderItem = forwardRef(({ label, value, ehr, ...others }, ref) => (
  <div ref={ref} {...others}>
    {ehr ? `${label} · ${ehr}` : label}
  </div>
));

/**
 * The quarterly reports' provider picker, styled as DropdownSelect: providers approved for this
 * contractor, of `providerType` ("ogiso", "external", or "" for both), searched on the server
 * as you type. It used to list the first page of providers (10), filtered in the browser, so
 * an approved provider past it could never be picked (OD-1694).
 *
 * `selectedLabel` names `value` until a search lists it. `withEhr` shows each provider's EHR.
 * onSelect gets { value, label }.
 */
const ProviderPicker = ({
  value,
  selectedLabel,
  providerType = "",
  withEhr = false,
  onSelect,
  placeholder = "Select a provider",
}) => {
  const { classes } = useDrawerStyles();

  const fetchOptions = async (search) => {
    const { providers, count } = await searchSkypointProviders(search, {
      providerType,
      approval: "approved",
    });
    return {
      options: providers.map((p) => ({
        value: p.id,
        label: providerLabel(p),
        ...(withEhr ? { ehr: ehrOf(p) } : {}),
      })),
      total: count,
    };
  };

  return (
    <RemoteSearchSelect
      value={value || null}
      selectedOption={value ? { value, label: selectedLabel || "" } : null}
      fetchOptions={fetchOptions}
      noun="providers"
      nothingFound="No approved provider found"
      itemComponent={ProviderItem}
      onChange={(picked, option) =>
        picked && onSelect?.({ value: picked, label: option?.label || "" })
      }
      placeholder={placeholder}
      rightSection={<IconChevronDown size={15} color="#9CA3AF" />}
      withinPortal
      zIndex={1200}
      maxDropdownHeight={320}
      classNames={{ input: classes.select, dropdown: classes.ddPop, item: classes.ddOpt }}
      styles={{ rightSection: { pointerEvents: "none" } }}
    />
  );
};

export default ProviderPicker;
